/** The one place the client talks to the server.
 *
 * Every call is same-origin: in development Vite proxies /api to :5200, in
 * production nginx does. So there is no base URL to configure and no place for
 * a hardcoded host to rot.
 *
 * The server answers errors as `{error: "<machine_code>"}` with a status. That
 * code is what the UI branches on; the message is for the log, never the user.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: unknown;

  constructor(status: number, code: string, body: unknown) {
    super(`api ${status} ${code}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** True when the failure is "you are not signed in", which the router acts on. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

export function codeFrom(body: unknown, status: number): string {
  if (body && typeof body === "object" && "error" in body) {
    const value = (body as { error: unknown }).error;
    if (typeof value === "string" && value.length > 0) return value;
  }
  return `http_${status}`;
}

async function parse(response: Response): Promise<unknown> {
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) return null;
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const init: RequestInit = {
    method,
    credentials: "same-origin",
    headers: options.body === undefined ? {} : { "content-type": "application/json" },
    ...(options.signal ? { signal: options.signal } : {}),
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);

  const response = await fetch(path, init);
  const body = await parse(response);
  if (!response.ok) throw new ApiError(response.status, codeFrom(body, response.status), body);
  return body as T;
}

export interface Health {
  ok: boolean;
  sha: string;
  db: "up" | "down";
  now: string;
}

export function getHealth(signal?: AbortSignal): Promise<Health> {
  return request<Health>("/api/health", signal ? { signal } : {});
}
